"use client";

import * as React from "react";
import { initSiraSupport, type SiraSupportConfig, type SiraSupportHandle } from "./index.js";

// Module-level singleton so the controller survives React Strict Mode
// double-invokes and Fast Refresh remounts. Without this, any React cleanup
// (HMR file save, parent re-render) calls destroy() and kills a live session.
let activeHandle: SiraSupportHandle | null = null;
let pendingDestroy: ReturnType<typeof setTimeout> | null = null;

/**
 * One-line drop-in for React / Next.js.
 *
 *   <SiraSupport publicKey="pk_live_xxx" />
 *
 * Mount it once (e.g. in `app/layout.tsx` or `_app.tsx`). It renders nothing
 * by default. Host app's Help menu opens the code-entry modal via
 * `window.__siraSupport?.openCodeEntry()` or the `useSiraSupport()` hook.
 */
export function SiraSupport(props: SiraSupportConfig): React.ReactElement | null {
  React.useEffect(() => {
    // Cancel any deferred destroy from the previous unmount cycle (Strict
    // Mode double-invoke, Fast Refresh, parent re-render, etc.).
    if (pendingDestroy !== null) {
      clearTimeout(pendingDestroy);
      pendingDestroy = null;
    }
    if (!activeHandle) {
      activeHandle = initSiraSupport(props);
    }
    return () => {
      // Defer one task. If React immediately remounts (Strict Mode / HMR),
      // the next setup will cancel this before it fires — live session
      // survives. Only a genuine unmount (page unload) lets it through.
      pendingDestroy = setTimeout(() => {
        activeHandle?.destroy();
        activeHandle = null;
        pendingDestroy = null;
      }, 0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.publicKey, props.serverUrl]);
  return null;
}

/**
 * Hook that returns a handle for triggering the code-entry modal from a
 * Help menu item. Safe to call before mount — returns a no-op handle then.
 */
export function useSiraSupport(): Pick<SiraSupportHandle, "openCodeEntry" | "end"> {
  return {
    openCodeEntry: () => window.__siraSupport?.openCodeEntry(),
    end: (reason?: string) => window.__siraSupport?.end(reason),
  };
}

/**
 * Convenience trigger: a <button> that opens the code-entry modal.
 * Unstyled by default so it inherits host styles.
 */
export function SiraSupportTrigger(
  props: React.ButtonHTMLAttributes<HTMLButtonElement>,
): React.ReactElement {
  const { openCodeEntry } = useSiraSupport();
  return (
    <button
      type="button"
      {...props}
      onClick={(e) => {
        props.onClick?.(e);
        if (!e.defaultPrevented) openCodeEntry();
      }}
    >
      {props.children ?? "Enter support code"}
    </button>
  );
}
